import React, { useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Check, ChevronRight, Clock, Search, X } from 'lucide-react';
interface ModelOption {
  name: string;
  category: 'ai' | 'nwp' | 'live';
  liveOnly?: boolean;
  hourlyRuns?: boolean;
}
const DEFAULT_ALL_MODELS: ModelOption[] = [
// AI Models
{
  name: 'EPT-2',
  category: 'ai'
}, {
  name: 'EPT-2 Early',
  category: 'ai'
}, {
  name: 'EPT-2 RR',
  category: 'ai'
}, {
  name: 'EPT-2 HRRR',
  category: 'ai',
  hourlyRuns: true
}, {
  name: 'EPT-2e',
  category: 'ai'
}, {
  name: 'EC AIFS',
  category: 'ai'
}, {
  name: 'Microsoft Aurora',
  category: 'ai'
}, {
  name: 'EC ENS',
  category: 'ai'
},
// NWP Models
{
  name: 'EC IFS',
  category: 'nwp'
}, {
  name: 'GFS',
  category: 'nwp'
}, {
  name: 'ICON Global',
  category: 'nwp'
}, {
  name: 'ICON-EU',
  category: 'nwp'
}, {
  name: 'UKMO Global (10km)',
  category: 'nwp'
},
// Ensemble / Live
{
  name: 'EC Ensemble Mean',
  category: 'live',
  liveOnly: true
}, {
  name: 'GFS Ensemble Mean',
  category: 'live',
  liveOnly: true
}];
export interface TableModelDropdownProps {
  allModels?: ModelOption[];
  /** Map of modelName -> array of selected run ISO strings (empty string = latest/default) */
  selectedModelRuns: Record<string, string[]>;
  onToggleModel: (name: string) => void;
  onToggleRun: (modelName: string, runValue: string) => void;
  onClearAll?: () => void;
  availableRuns: {
    label: string;
    value: string;
    date: Date;
  }[];
  availableHourlyRuns?: {
    label: string;
    value: string;
    date: Date;
  }[];
  style: React.CSSProperties;
}
const dropdownStyle: React.CSSProperties = {
  zIndex: 9999,
  background: 'rgba(15, 23, 42, 0.55)',
  backdropFilter: 'blur(40px) saturate(180%)',
  WebkitBackdropFilter: 'blur(40px) saturate(180%)'
};
function RunSubmenu({
  modelName,
  availableRuns,
  selectedRunValues,
  onToggleRun,
  position,
  onMouseEnter,
  onMouseLeave
}: {
  modelName: string;
  availableRuns: {
    label: string;
    value: string;
    date: Date;
  }[];
  selectedRunValues: string[];
  onToggleRun: (modelName: string, runValue: string) => void;
  position: {
    top: number;
    left: number;
  };
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}) {
  return ReactDOM.createPortal(<div data-portal-menu className="fixed w-52 rounded-xl shadow-2xl shadow-black/50 border border-white/[0.08] overflow-hidden flex flex-col" style={{
    ...dropdownStyle,
    top: position.top,
    left: position.left,
    maxHeight: 340
  }} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} data-id="element-1836">
      <div className="absolute inset-0 rounded-xl pointer-events-none" style={{
      background: 'linear-gradient(135deg, rgba(255,255,255,0.06) 0%, transparent 50%, rgba(255,255,255,0.02) 100%)'
    }} data-id="element-1837" />
      <div className="relative px-3 py-1.5 text-[9px] font-bold text-white/30 uppercase tracking-widest flex items-center gap-1.5 flex-shrink-0" data-id="element-1838">
        <Clock size={9} data-id="element-1839" /> Forecast Runs
      </div>
      <div className="relative overflow-y-auto flex-1 py-0.5 scrollbar-thin" data-id="element-1840">
        {availableRuns.map(run => {
        const isRunSelected = selectedRunValues.includes(run.value);
        return <button key={run.value} onClick={() => onToggleRun(modelName, run.value)} className="relative w-full px-3 py-1.5 text-left text-[11px] text-slate-300 hover:bg-white/[0.06] hover:text-white flex items-center justify-between transition-colors" data-id="element-1841">
              <span className="font-mono" data-id="element-1842">{run.label}</span>
              {isRunSelected && <Check size={10} className="text-[#EC4899] flex-shrink-0" data-id="element-1843" />}
            </button>;
      })}
      </div>
    </div>, document.body);
}
export function TableModelDropdown({
  allModels = DEFAULT_ALL_MODELS,
  selectedModelRuns,
  onToggleModel,
  onToggleRun,
  onClearAll,
  availableRuns,
  availableHourlyRuns,
  style
}: TableModelDropdownProps) {
  const [modelSearch, setModelSearch] = useState('');
  const [hoveredModel, setHoveredModel] = useState<string | null>(null);
  const [submenuPos, setSubmenuPos] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const filteredModels = allModels.filter(m => m.name.toLowerCase().includes(modelSearch.toLowerCase()));
  const aiModels = filteredModels.filter(m => m.category === 'ai');
  const nwpModels = filteredModels.filter(m => m.category === 'nwp');
  const liveModels = filteredModels.filter(m => m.category === 'live');
  const handleMouseEnter = (name: string, e: React.MouseEvent) => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    setHoveredModel(name);
    const rect = e.currentTarget.getBoundingClientRect();
    setSubmenuPos({
      top: rect.top,
      left: rect.right + 4
    });
  };
  const handleMouseLeave = () => {
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredModel(null);
      setSubmenuPos(null);
    }, 200);
  };
  const handleSubmenuEnter = () => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
  };
  const renderModelRow = (m: ModelOption, showSubmenu: boolean) => {
    const runs = selectedModelRuns[m.name] || [];
    const isSelected = runs.length > 0;
    const isHovered = hoveredModel === m.name;
    const runCount = runs.length;
    const runsForModel = m.hourlyRuns && availableHourlyRuns ? availableHourlyRuns : availableRuns;
    return <div key={m.name} className="relative" onMouseEnter={e => handleMouseEnter(m.name, e)} onMouseLeave={handleMouseLeave} data-id="element-1844">
        <button onClick={() => onToggleModel(m.name)} className="relative w-full px-3 py-1.5 text-left text-xs text-slate-300 hover:bg-white/[0.06] hover:text-white flex items-center justify-between transition-colors" data-id="element-1845">
          <div className="flex flex-col min-w-0" data-id="element-1846">
            <span className="truncate" data-id="element-1847">{m.name}</span>
            {m.hourlyRuns && <span className="text-[9px] text-[#EC4899]/50" data-id="element-1848">Hourly</span>}
            {m.liveOnly && <span className="text-[9px] text-white/20" data-id="element-1849">Live only</span>}
          </div>
          <div className="flex items-center gap-1.5" data-id="element-1850">
            {runCount > 1 && <span className="text-[9px] text-[#EC4899]/70 font-medium bg-[#EC4899]/10 px-1 rounded" data-id="element-1851">
                {runCount} runs
              </span>}
            {runCount === 1 && runs[0] && <span className="text-[9px] text-white/30 font-mono" data-id="element-1852">
                {new Date(runs[0]).getUTCHours().toString().padStart(2, '0')}z
              </span>}
            {isSelected && <Check size={12} className="text-[#EC4899] flex-shrink-0" data-id="element-1853" />}
            <ChevronRight size={10} className="text-white/20 flex-shrink-0" data-id="element-1854" />
          </div>
        </button>
        {showSubmenu && isHovered && submenuPos && <RunSubmenu modelName={m.name} availableRuns={runsForModel} selectedRunValues={runs} onToggleRun={onToggleRun} position={submenuPos} onMouseEnter={handleSubmenuEnter} onMouseLeave={handleMouseLeave} data-id="element-1855" />}
      </div>;
  };
  return <div data-portal-menu className="fixed w-64 rounded-xl shadow-2xl shadow-black/50 border border-white/[0.08] overflow-hidden flex flex-col" style={{
    ...style,
    maxHeight: 420
  }} data-id="element-1856">
      <div className="absolute inset-0 rounded-xl pointer-events-none" style={{
      background: 'linear-gradient(135deg, rgba(255,255,255,0.06) 0%, transparent 50%, rgba(255,255,255,0.02) 100%)'
    }} data-id="element-1857" />
      <div className="relative p-2 border-b border-white/[0.06]" data-id="element-1858">
        <Search size={12} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30" data-id="element-1859" />
        <input value={modelSearch} onChange={e => setModelSearch(e.target.value)} placeholder="Search models..." className="relative w-full h-7 pl-7 pr-2 rounded-md bg-white/[0.04] border border-white/[0.08] text-[11px] text-white/90 placeholder:text-white/25 focus:outline-none focus:border-[#EC4899]/40" autoFocus data-id="element-1860" />
      </div>
      <div className="relative overflow-y-auto flex-1 py-1 scrollbar-thin" data-id="element-1861">
        {aiModels.length > 0 && <>
            <div className="px-3 py-1.5 text-[9px] font-bold text-white/30 uppercase tracking-widest" data-id="element-1862">
              AI Models
            </div>
            {aiModels.map(m => renderModelRow(m, true))}
          </>}
        {nwpModels.length > 0 && <>
            <div className="px-3 py-1.5 mt-1 text-[9px] font-bold text-white/30 uppercase tracking-widest" data-id="element-1863">
              NWP Models
            </div>
            {nwpModels.map(m => renderModelRow(m, true))}
          </>}
        {liveModels.length > 0 && <>
            <div className="px-3 py-1.5 mt-1 text-[9px] font-bold text-white/30 uppercase tracking-widest" data-id="element-1864">
              Other Models
            </div>
            {liveModels.map(m => {
          const runs = selectedModelRuns[m.name] || [];
          const isSelected = runs.length > 0;
          return <button key={m.name} onClick={() => onToggleModel(m.name)} className="relative w-full px-3 py-1.5 text-left text-xs text-slate-300 hover:bg-white/[0.06] hover:text-white flex items-center justify-between transition-colors" data-id="element-1865">
                  <div className="flex flex-col min-w-0" data-id="element-1866">
                    <span className="truncate" data-id="element-1867">{m.name}</span>
                    <span className="text-[9px] text-white/20" data-id="element-1868">Live only</span>
                  </div>
                  {isSelected && <Check size={12} className="text-[#EC4899] flex-shrink-0" data-id="element-1869" />}
                </button>;
        })}
          </>}
        {filteredModels.length === 0 && <div className="px-3 py-4 text-center text-[11px] text-white/25" data-id="element-1870">
            No models found
          </div>}
      </div>
      {/* Clear all footer */}
      {onClearAll && Object.keys(selectedModelRuns).some(k => (selectedModelRuns[k] || []).length > 0) && <div className="relative border-t border-white/[0.06] p-1.5" data-id="element-1871">
            <button onClick={onClearAll} className="w-full px-3 py-1.5 rounded-md text-[11px] font-medium text-slate-400 hover:text-red-400 hover:bg-red-500/10 flex items-center justify-center gap-1.5 transition-colors" data-id="element-1872">
              <X size={10} data-id="element-1873" />
              Clear all models
            </button>
          </div>}
    </div>;
}